import { ChatCircleIcon, WarningCircle } from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { useDebounce } from "use-debounce";

import AdminTablePagination from "../../../components/adminDashboard/AdminTable/AdminTablePagination";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomDropdown from "../../../components/common/general/CustomDropDown/CustomDropdown";
import CustomSearchInput from "../../../components/common/general/CustomSearchInput/CustomSearchInput";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { CourseInsightsRiskLevel, useGetCourseRosterInsightsQuery } from "@axio/graphql/generated/graphql";
import { colors, radii, shadowStyles, spacing, webTransitions } from "../../../styles/design-system";
import { clampPercentage, formatPercentage } from "../../../utils/analyticsUtils";
import CourseRosterInsightsTable, { getRiskBadgeStyle, toInitials } from "./CourseRosterInsightsTable";
import { NavigationProp, RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { RootStackParamList } from "../../../navigation/navigation.types";

interface AdminCourseAnalyticsContainerProps {
  courseId: string;
}

const RISK_FILTER_OPTIONS = [
  { label: "All Risk Levels", value: "ALL" },
  { label: "At Risk", value: CourseInsightsRiskLevel.AtRisk },
  { label: "Watch", value: CourseInsightsRiskLevel.Watch },
  { label: "On Track", value: CourseInsightsRiskLevel.OnTrack },
];

const topicCardColors = {
  borderColor: colors.accent.purple200,
  hoverBorderColor: "#C4B5FD",
  hoverBackgroundColor: colors.purple[25],
  hoverTextColor: colors.accent.purple500,
  countBackgroundColor: colors.grey[100],
  countTextColor: colors.grey[600],
  hoverCountBackgroundColor: colors.purple[50],
  hoverCountTextColor: colors.accent.purple500,
} as const;

const AdminCourseAnalyticsContainer: React.FC<AdminCourseAnalyticsContainerProps> = ({ courseId }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [riskFilter, setRiskFilter] = useState<"ALL" | CourseInsightsRiskLevel>("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [riskFilterOpen, setRiskFilterOpen] = useState(false);
  const [showAllConcepts, setShowAllConcepts] = useState(false);
  const [hoveredRiskStudentId, setHoveredRiskStudentId] = useState<string | null>(null);
  const [hoveredTopicId, setHoveredTopicId] = useState<string | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Course Information For Particular Student">>();
  const [debouncedSearchQuery] = useDebounce(searchQuery, 350);

  const { courseName } = route.params || {};
  const handleNavigateToStudentDetail = (studentId: string, studentName: string) => {
    navigation.navigate("Course Information For Particular Student", {
      courseId,
      courseName,
      studentId,
      studentName,
      returnTo: "Course Information",
    });
  };
  const handleAttentionItemPress = () => undefined;
  const handleSelectedStudentsMessage = () => undefined;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, riskFilter]);

  const { data, loading, error } = useGetCourseRosterInsightsQuery({
    variables: {
      courseId,
      riskFilter: riskFilter === "ALL" ? undefined : riskFilter,
      searchQuery: debouncedSearchQuery.trim() || undefined,
      page,
      pageSize,
      sortBy: "risk_desc",
    },
    skip: !courseId,
    fetchPolicy: "cache-and-network",
  });

  const payload = data?.getCourseRosterInsights?.data;
  const rosterStudents = payload?.students ?? [];

  useEffect(() => {
    setSelectedStudentIds((current) =>
      current.filter((studentId) => rosterStudents.some((student) => student.userId === studentId))
    );
  }, [rosterStudents]);

  if (loading && !payload) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color={colors.brand[500]} />
      </View>
    );
  }

  if (error || !payload) {
    return (
      <View style={styles.errorContainer}>
        <CustomText
          text={error?.message || "Failed to load analytics insights"}
          size="m"
          weight="medium"
          useTranslationText={false}
        />
      </View>
    );
  }

  const { needsAttention, cohortSummary, totalCount } = payload;
  const { atRiskStudents, commonConfusionTopics, summary } = needsAttention;
  const averageCompletion = clampPercentage(cohortSummary.avgCompletionPct);
  const averageGradeText =
    typeof cohortSummary.avgGradePct === "number" ? formatPercentage(cohortSummary.avgGradePct) : "—";
  const negativeSentimentRatio = clampPercentage(cohortSummary.negativeSentimentPct);
  const selectedStudentCount = selectedStudentIds.length;

  return (
    <View style={styles.wrapper}>
      <View style={[styles.needsAttentionCard, shadowStyles.xs]}>
        <View style={styles.needsAttentionHeader}>
          <View style={styles.needsAttentionTitleRow}>
            <WarningCircle size={24} color={colors.grey[950]} />
            <CustomText text="Needs Your Attention" weight="bold" size="xl" useTranslationText={false} />
          </View>
          <CustomText
            text={`${summary.atRiskCount} at-risk • ${Math.round(averageCompletion)}% avg completion • ${averageGradeText} avg score • ${Math.round(negativeSentimentRatio)}% negative sentiment`}
            size="s"
            useTranslationText={false}
            style={styles.summaryLine}
          />
        </View>

        <View style={styles.attentionColumns}>
          <View style={styles.attentionColumn}>
            <CustomText
              text="At-Risk Students"
              size="s"
              weight="bold"
              useTranslationText={false}
              style={styles.columnTitle}
            />
            {atRiskStudents.length === 0 ? (
              <CustomText
                text="No at-risk students for this snapshot."
                size="s"
                useTranslationText={false}
                style={styles.emptyText}
              />
            ) : (
              atRiskStudents.slice(0, 3).map((student) => {
                const riskStyle = getRiskBadgeStyle(student.riskLevel);
                const isHovered = hoveredRiskStudentId === student.userId;

                return (
                  <Pressable
                    key={student.userId}
                    accessibilityRole="button"
                    onPress={() => handleNavigateToStudentDetail(student.userId, student.studentName)}
                    onHoverIn={() => setHoveredRiskStudentId(student.userId)}
                    onHoverOut={() => setHoveredRiskStudentId(null)}
                    style={[
                      styles.attentionListItem,
                      styles.attentionPressableItem,
                      {
                        borderColor: isHovered ? riskStyle.hoverBorderColor : colors.grey[200],
                        borderLeftWidth: 2,
                        borderLeftColor: isHovered ? riskStyle.hoverBorderColor : riskStyle.attentionBorderColor,
                      },
                      isHovered && { backgroundColor: riskStyle.hoverBackgroundColor },
                    ]}>
                    <View style={styles.avatar}>
                      <CustomText
                        text={toInitials(student.studentName)}
                        size="xs"
                        weight="bold"
                        useTranslationText={false}
                        style={styles.avatarText}
                      />
                    </View>
                    <View style={styles.attentionListItemText}>
                      <CustomText
                        text={student.studentName}
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={isHovered && { color: riskStyle.hoverTextColor }}
                      />
                      <CustomText
                        text={`${formatPercentage(student.progressPct)} complete`}
                        size="xs"
                        useTranslationText={false}
                        style={[styles.mutedText, isHovered && { color: riskStyle.hoverMetaColor }]}
                      />
                    </View>
                    <CustomText
                      text="View ->"
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={[styles.attentionActionText, isHovered && { color: riskStyle.hoverTextColor }]}
                    />
                  </Pressable>
                );
              })
            )}
          </View>

          <View style={styles.attentionColumn}>
            <CustomText
              text="Common Confusion Points"
              size="s"
              weight="bold"
              useTranslationText={false}
              style={styles.columnTitle}
            />
            {commonConfusionTopics.length === 0 ? (
              <CustomText
                text="No confusion points detected yet."
                size="s"
                useTranslationText={false}
                style={styles.emptyText}
              />
            ) : (
              (showAllConcepts ? commonConfusionTopics : commonConfusionTopics.slice(0, 3)).map((topic) => {
                const isHovered = hoveredTopicId === topic.topicId;

                return (
                  <Pressable
                    key={topic.topicId}
                    accessibilityRole="button"
                    onPress={handleAttentionItemPress}
                    onHoverIn={() => setHoveredTopicId(topic.topicId)}
                    onHoverOut={() => setHoveredTopicId(null)}
                    style={[
                      styles.attentionListItem,
                      styles.attentionPressableItem,
                      styles.attentionTopicItem,
                      {
                        borderColor: isHovered ? topicCardColors.hoverBorderColor : colors.grey[200],
                        borderLeftColor: isHovered ? topicCardColors.hoverBorderColor : topicCardColors.borderColor,
                      },
                      isHovered && { backgroundColor: topicCardColors.hoverBackgroundColor },
                    ]}>
                    <View style={styles.attentionListItemText}>
                      <CustomText
                        text={topic.summary || topic.title}
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={isHovered && { color: topicCardColors.hoverTextColor }}
                      />
                      <CustomText
                        text={
                          topic.moduleTitle
                            ? `in ${topic.moduleTitle} • Click to filter`
                            : "Course-wide • Click to filter"
                        }
                        size="xs"
                        useTranslationText={false}
                        style={[styles.mutedText, isHovered && { color: topicCardColors.hoverTextColor }]}
                      />
                    </View>
                    <View
                      style={[
                        styles.countBadge,
                        {
                          backgroundColor: isHovered
                            ? topicCardColors.hoverCountBackgroundColor
                            : topicCardColors.countBackgroundColor,
                        },
                      ]}>
                      <CustomText
                        text={`${topic.count}`}
                        size="xs"
                        weight="bold"
                        useTranslationText={false}
                        style={[
                          styles.countBadgeText,
                          { color: isHovered ? topicCardColors.hoverCountTextColor : topicCardColors.countTextColor },
                        ]}
                      />
                    </View>
                  </Pressable>
                );
              })
            )}
            {commonConfusionTopics.length > 3 ? (
              <Pressable style={styles.viewAllConceptsButton} onPress={() => setShowAllConcepts((prev) => !prev)}>
                <CustomText
                  text={showAllConcepts ? "← Show less" : `View all ${commonConfusionTopics.length} concepts →`}
                  size="xs"
                  useTranslationText={false}
                  style={styles.inlineLinkText}
                />
              </Pressable>
            ) : null}
          </View>
        </View>
      </View>

      <View style={styles.tableCard}>
        <View style={styles.tableTopBar}>
          <View style={styles.tableTitleArea}>
            <CustomText text="Roster Insights" size="xl" weight="bold" useTranslationText={false} />
            <CustomText
              text={`${totalCount} students`}
              size="xs"
              useTranslationText={false}
              style={styles.tableStudentCount}
            />
          </View>

          <View style={styles.tableFilters}>
            <View style={styles.searchContainer}>
              <CustomSearchInput
                placeholder="Search students..."
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />
            </View>

            <View style={styles.filterDropdown}>
              <CustomDropdown
                options={RISK_FILTER_OPTIONS}
                value={riskFilter}
                onChange={(value) => setRiskFilter(value as "ALL" | CourseInsightsRiskLevel)}
                placeholder="Risk Filter"
                open={riskFilterOpen}
                onOpenChange={setRiskFilterOpen}
                fullWidth={false}
                accessibilityLabel="Risk Filter"
                dropdownStyle={styles.dropdownStyle}
                dropdownContainerStyle={styles.dropdownMenu}
              />
            </View>
          </View>
        </View>

        {selectedStudentCount > 0 ? (
          <View style={styles.selectionBanner}>
            <View style={styles.selectionInfo}>
              <View style={styles.selectionCountBadge}>
                <CustomText
                  text={`${selectedStudentCount}`}
                  size="xs"
                  weight="bold"
                  useTranslationText={false}
                  style={styles.selectionCountText}
                />
              </View>
              <CustomText
                text={`${selectedStudentCount} student${selectedStudentCount === 1 ? "" : "s"} selected`}
                size="s"
                weight="medium"
                useTranslationText={false}
                style={styles.selectionInfoText}
              />
            </View>

            <View style={styles.selectionActions}>
              <CustomButton
                text="Send Message to Selected"
                useTranslationText={false}
                size="xs"
                styleType="primary"
                leftIcon={<ChatCircleIcon size={16} color={colors.white} weight="regular" />}
                onPress={handleSelectedStudentsMessage}
                style={styles.selectionMessageButtonSize}
                textStyle={styles.selectionMessageButtonText}
              />

              <Pressable style={styles.clearSelectionButton} onPress={() => setSelectedStudentIds([])}>
                <CustomText
                  text="Clear Selection"
                  size="xs"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.clearSelectionText}
                />
              </Pressable>
            </View>
          </View>
        ) : null}

        <View style={[styles.tableSurface, shadowStyles.xs]}>
          <CourseRosterInsightsTable
            students={rosterStudents}
            selectedStudentIds={selectedStudentIds}
            onSelectionChange={setSelectedStudentIds}
          />

          <AdminTablePagination
            currentPage={page}
            totalItems={totalCount}
            totalPages={Math.max(1, Math.ceil(totalCount / pageSize))}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={setPageSize}
            itemsPerPageOptions={[8, 12, 20, 40]}
            showItemsPerPageSelector={true}
            navigationStyle="icons"
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    padding: spacing.lg,
    gap: spacing.lg,
    backgroundColor: "transparent",
  },
  loaderContainer: {
    minHeight: 240,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    minHeight: 180,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    margin: spacing.lg,
    padding: spacing.lg,
  },
  needsAttentionCard: {
    backgroundColor: "rgba(255,255,255,0.9)",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.xl,
    overflow: "hidden",
  },
  needsAttentionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  needsAttentionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  summaryLine: {
    color: colors.grey[500],
    textAlign: "right",
    alignSelf: "center",
  },
  attentionColumns: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
    padding: spacing.md,
  },
  attentionColumn: {
    flex: 1,
    minWidth: 280,
    gap: spacing.xs,
  },
  columnTitle: {
    color: colors.grey[700],
  },
  attentionListItem: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: spacing.sm,
    gap: 12,
    backgroundColor: colors.white,
    ...webTransitions.fast,
  },
  attentionPressableItem: {
    // @ts-expect-error web-only property
    cursor: "pointer",
  },
  attentionTopicItem: {
    borderLeftWidth: 2,
    borderLeftColor: topicCardColors.borderColor,
  },
  attentionListItemText: {
    flex: 1,
    gap: 2,
  },
  viewAllConceptsButton: {
    marginTop: spacing.xs,
    alignSelf: "center",
  },
  inlineLinkText: {
    color: colors.grey[500],
  },
  emptyText: {
    color: colors.grey[500],
  },
  countBadge: {
    minWidth: 24,
    height: 24,
    borderRadius: radii.full,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: spacing.xs,
  },
  countBadgeText: {
    color: colors.grey[600],
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: radii.full,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.brand[50],
    borderWidth: 1,
    borderColor: colors.brand[100],
  },
  avatarText: {
    color: colors.brand[600],
  },
  attentionActionText: {
    color: colors.grey[500],
  },
  tableCard: {
    backgroundColor: "transparent",
    gap: spacing.md,
    alignItems: "stretch",
  },
  tableTopBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 0,
    paddingVertical: 0,
    gap: spacing.md,
    flexWrap: "wrap",
  },
  tableTitleArea: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  tableStudentCount: {
    color: colors.grey[500],
  },
  tableFilters: {
    flexDirection: "row",
    gap: spacing.sm,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    flex: 1,
  },
  searchContainer: {
    minWidth: 100,
    width: 250,
    flexShrink: 1,
  },
  filterDropdown: {
    zIndex: 5000,
    elevation: 5000,
  },
  dropdownStyle: {
    borderColor: colors.grey[200],
    minWidth: 180,
  },
  dropdownMenu: {
    borderColor: colors.grey[200],
    borderRadius: 14,
  },
  tableSurface: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "hidden",
  },
  selectionBanner: {
    marginHorizontal: 0,
    marginBottom: 0,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: "#C4B5FD",
    backgroundColor: "#F5F0FF",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.md,
    flexWrap: "wrap",
  },
  selectionInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  selectionCountBadge: {
    width: 32,
    height: 32,
    borderRadius: radii.full,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#8256E4",
  },
  selectionCountText: {
    color: colors.white,
  },
  selectionInfoText: {
    color: "#6941C6",
  },
  selectionActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    flexWrap: "wrap",
  },
  selectionMessageButtonSize: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    borderRadius: radii.md,
    paddingHorizontal: spacing.sm,
    paddingVertical: 10,
  },
  selectionMessageButtonText: {
    color: colors.white,
  },
  clearSelectionButton: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 10,
    ...webTransitions.fast,
    // @ts-expect-error web-only property
    cursor: "pointer",
  },
  clearSelectionText: {
    color: colors.grey[700],
  },
  mutedText: {
    color: colors.grey[500],
  },
});

export default AdminCourseAnalyticsContainer;
